/**
 * Budget usage panel — per-initiative progress bars
 * @module components/initiatives/budget-usage-chart
 */

import { useMemo } from 'react';
import { PiggyBank } from 'lucide-react';
import { formatPoints, parseApiFloat } from '@/lib/utils/format';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { BudgetUsage } from '@/types/initiative';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

const getLocalizedTitle = (title: string | null, category: string, locale: Locale): string => {
  // If title is provided and not a technical type, use it
  if (title && !title.includes('_initiative') && !title.includes('_saver')) {
    return title;
  }

  // Otherwise, translate the category/type
  switch (category) {
    case 'walk_initiative':
      return locale === 'ar' ? 'مبادرة المشي' : 'Walking Initiative';
    case 'juthoor_initiative':
      return locale === 'ar' ? 'مبادرة جذور' : 'Juthoor Initiative';
    default:
      return title || category;
  }
};

// Bar color reflects how close an initiative is to exhausting its budget
function usageColorClass(percent: number): string {
  if (percent >= 90) return 'bg-red-500 dark:bg-red-400';
  if (percent >= 70) return 'bg-amber-500 dark:bg-amber-400';
  return 'bg-primary';
}

interface BudgetUsageChartProps {
  budgetUsage: BudgetUsage[];
  locale: Locale;
  isDark: boolean;
}

/**
 * Display per-initiative budget usage as a scrollable list of progress bars
 */
export function BudgetUsageChart({ budgetUsage, locale }: BudgetUsageChartProps) {
  // Highest usage first — the initiatives closest to running out surface at the top
  const sortedUsage = useMemo(() => {
    return [...(budgetUsage ?? [])].sort(
      (a, b) => b.usage_percentage - a.usage_percentage
    );
  }, [budgetUsage]);

  if (sortedUsage.length === 0) {
    return (
      <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">
          {t(locale, 'budgetUsage')}
        </h3>
        <EmptyState
          icon={PiggyBank}
          title={t(locale, 'noData')}
          description={t(locale, 'noDataAvailable')}
        />
      </div>
    );
  }

  return (
    <div className="bg-card p-6 rounded-lg shadow-sm border border-border flex flex-col h-full">
      <h3 className="text-lg font-semibold text-foreground mb-6 shrink-0">
        {t(locale, 'budgetUsage')}
      </h3>
      <div className="flex-1 overflow-y-auto max-h-[420px] space-y-5 pe-1">
        {sortedUsage.map((item) => {
          const title =
            (locale === 'ar' ? item.title_ar : item.title) ||
            getLocalizedTitle(null, item.category, locale);
          const used = parseApiFloat(item.budget_used);
          const total = parseApiFloat(item.init_budget);
          const percent = Math.min(Math.max(item.usage_percentage, 0), 100);

          return (
            <div key={item.id} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-foreground truncate font-medium">{title}</span>
                <span className="text-foreground-secondary shrink-0 tabular-nums">
                  {formatPoints(String(used), locale)} / {formatPoints(String(total), locale)} NC
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-border overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all duration-500', usageColorClass(percent))}
                  style={{ width: `${percent}%` }}
                  role="progressbar"
                  aria-valuenow={Math.round(percent)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={title}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
